/**
 * Account Tray — the identity offboarding console (ADR-0077 S4).
 *
 * The identity ⇄ workspace graph lives ONLY on Control, so this page answers
 * "who is this person across the platform?" and executes the ADR's headline
 * promise: offboarding is ONE action. Disabling an identity kills its central
 * sessions instantly AND propagates over the backbone to every membership
 * workspace (the linked operator row is disabled + kicked out). A row expands
 * to its workspaces, each with a per-workspace removal.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronDown, ChevronRight, Search, ShieldOff, UserCheck, Users, X } from "lucide-react";
import * as React from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { EmptyState, PageContainer, PageContent, PageHeader } from "@/components/ui/page";
import { Table, Tbody, Td, Th, Thead, Tr } from "@/components/ui/table";
import { type PlatformIdentity, platformApi } from "@/lib/platform";

const IDENTITIES_KEY = ["platform", "identities"];

function stateBadge(identity: PlatformIdentity) {
  if (identity.state === "disabled") return <Badge tone="deprecated">Disabilitato</Badge>;
  if (!identity.claimed) return <Badge>Invito in sospeso</Badge>;
  return <Badge tone="active">Attivo</Badge>;
}

export function PlatformIdentitiesPage() {
  const qc = useQueryClient();
  const [search, setSearch] = React.useState("");
  const [q, setQ] = React.useState("");
  const [expanded, setExpanded] = React.useState<string | null>(null);

  const listQ = useQuery({
    queryKey: [...IDENTITIES_KEY, q],
    queryFn: () => platformApi.listIdentities({ q: q || undefined, page_size: 100 }),
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: IDENTITIES_KEY });
  const disable = useMutation({
    mutationFn: (id: string) => platformApi.disableIdentity(id),
    onSuccess: invalidate,
  });
  const enable = useMutation({
    mutationFn: (id: string) => platformApi.enableIdentity(id),
    onSuccess: invalidate,
  });
  const removeMembership = useMutation({
    mutationFn: (p: { id: string; tenantId: string }) =>
      platformApi.removeIdentityMembership(p.id, p.tenantId),
    onSuccess: invalidate,
  });

  const items = listQ.data?.items ?? [];

  return (
    <PageContainer>
      <PageHeader
        title="Account Tray"
        description="Le identità centrali: chi è questa persona su tutta la piattaforma. Disabilitare = offboarding in un'azione (le sessioni muoiono ovunque)."
      />
      <PageContent>
        <form
          className="mb-4 flex max-w-md items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            setQ(search.trim());
          }}
        >
          <Input
            placeholder="Cerca per email o nome…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <Button type="submit" variant="outline" size="icon" aria-label="Cerca">
            <Search className="h-4 w-4" strokeWidth={1.5} />
          </Button>
        </form>

        {items.length > 0 ? (
          <div className="rounded-lg overflow-hidden border border-border/60">
            <Table>
              <Thead>
                <Tr>
                  <Th className="w-8" />
                  <Th>Email</Th>
                  <Th>Nome</Th>
                  <Th>Stato</Th>
                  <Th>Workspace</Th>
                  <Th>Ultimo accesso</Th>
                  <Th className="text-right">Azioni</Th>
                </Tr>
              </Thead>
              <Tbody>
                {items.map((identity) => (
                  <React.Fragment key={identity.id}>
                    <Tr>
                      <Td>
                        <button
                          type="button"
                          aria-label={`Espandi ${identity.email}`}
                          className="text-muted-foreground"
                          onClick={() =>
                            setExpanded((c) => (c === identity.id ? null : identity.id))
                          }
                        >
                          {expanded === identity.id ? (
                            <ChevronDown className="h-4 w-4" strokeWidth={1.5} />
                          ) : (
                            <ChevronRight className="h-4 w-4" strokeWidth={1.5} />
                          )}
                        </button>
                      </Td>
                      <Td className="font-medium">{identity.email}</Td>
                      <Td className="text-muted-foreground">{identity.full_name ?? "—"}</Td>
                      <Td>{stateBadge(identity)}</Td>
                      <Td className="text-muted-foreground">{identity.memberships.length}</Td>
                      <Td className="text-xs text-muted-foreground">
                        {identity.last_login_at
                          ? new Date(identity.last_login_at).toLocaleString()
                          : "—"}
                      </Td>
                      <Td className="text-right">
                        {identity.state === "disabled" ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => enable.mutate(identity.id)}
                            disabled={enable.isPending}
                          >
                            <UserCheck className="h-3.5 w-3.5" strokeWidth={1.5} /> Riattiva
                          </Button>
                        ) : (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-danger"
                            onClick={() => {
                              if (
                                window.confirm(
                                  `Disabilitare ${identity.email}? Le sue sessioni muoiono subito su TUTTI i workspace (${identity.memberships.length}).`,
                                )
                              )
                                disable.mutate(identity.id);
                            }}
                            disabled={disable.isPending}
                          >
                            <ShieldOff className="h-3.5 w-3.5" strokeWidth={1.5} /> Disabilita
                          </Button>
                        )}
                      </Td>
                    </Tr>
                    {expanded === identity.id && (
                      <Tr>
                        <Td colSpan={7} className="bg-muted/30 p-4">
                          {identity.memberships.length === 0 ? (
                            <span className="text-sm text-muted-foreground">
                              Nessun workspace collegato.
                            </span>
                          ) : (
                            <ul className="space-y-1.5">
                              {identity.memberships.map((m) => (
                                <li
                                  key={m.tenant_id}
                                  className="flex items-center justify-between gap-3 text-sm"
                                >
                                  <span>
                                    <span className="font-medium">{m.name ?? m.slug}</span>{" "}
                                    <span className="text-muted-foreground">({m.slug})</span>{" "}
                                    {m.status !== "active" && <Badge tone="deprecated">{m.status}</Badge>}
                                  </span>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-danger"
                                    onClick={() => {
                                      if (
                                        window.confirm(
                                          `Rimuovere ${identity.email} dal workspace "${m.slug}"? L'accesso lì termina subito.`,
                                        )
                                      )
                                        removeMembership.mutate({
                                          id: identity.id,
                                          tenantId: m.tenant_id,
                                        });
                                    }}
                                    disabled={removeMembership.isPending}
                                  >
                                    <X className="h-3.5 w-3.5" strokeWidth={1.5} /> Rimuovi
                                  </Button>
                                </li>
                              ))}
                            </ul>
                          )}
                        </Td>
                      </Tr>
                    )}
                  </React.Fragment>
                ))}
              </Tbody>
            </Table>
          </div>
        ) : (
          <EmptyState
            icon={Users}
            title={q ? "Nessuna identità trovata" : "Nessun Account Tray ancora"}
          />
        )}
      </PageContent>
    </PageContainer>
  );
}
